import { useEffect, useState } from 'react';
import { Download, WifiOff, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * PWA support UI: shows three distinct cues to the user.
 *
 *  1. **Install banner** — triggered by `beforeinstallprompt` on Chromium
 *     browsers. We hold the deferred prompt and expose a button that
 *     calls `prompt()` once the user acknowledges. Banner hides forever
 *     (per localStorage) after dismissal so we never nag.
 *
 *  2. **Update toast** — triggered by the `pwa-update-ready` window event
 *     that index.js dispatches when the registered SW has a new waiting
 *     worker. Clicking "Güncelle" posts `SKIP_WAITING` and the
 *     `controllerchange` listener in index.js then reloads the page.
 *
 *  3. **Offline bar** — listens for window `online`/`offline`. Shows a
 *     fixed-top amber strip so users know the reason a save failed.
 *
 * Mounted once in `DashboardLayout`, above the main `<Outlet />`.
 */
const INSTALL_DISMISS_KEY = 'pwa.installBannerDismissedAt';
const INSTALL_DISMISS_DAYS = 30; // re-surface every 30 days

export default function PWAStatus() {
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [offline, setOffline] = useState(!navigator.onLine);

  // ── Install prompt ────────────────────────────────────────────────
  useEffect(() => {
    // Only show install banner if:
    //   - we actually got a beforeinstallprompt (Chromium)
    //   - the app isn't already installed / running standalone
    //   - the user hasn't dismissed us within the last INSTALL_DISMISS_DAYS
    const alreadyStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: window-controls-overlay)').matches ||
      (window.navigator.standalone === true); // iOS Safari
    if (alreadyStandalone) return;

    let dismissedTooRecently = false;
    try {
      const raw = localStorage.getItem(INSTALL_DISMISS_KEY);
      if (raw) {
        const ageDays = (Date.now() - parseInt(raw, 10)) / (1000 * 60 * 60 * 24);
        dismissedTooRecently = ageDays < INSTALL_DISMISS_DAYS;
      }
    } catch { /* noop */ }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      if (!dismissedTooRecently) setShowInstall(true);
    };
    const onInstalled = () => {
      setShowInstall(false);
      setInstallPromptEvent(null);
      toast.success('OMS Portal ana ekrana eklendi');
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installPromptEvent) return;
    try {
      installPromptEvent.prompt();
      const { outcome } = await installPromptEvent.userChoice;
      if (outcome !== 'accepted') {
        try { localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now())); } catch {}
      }
    } finally {
      setShowInstall(false);
      setInstallPromptEvent(null);
    }
  };

  const dismissInstall = () => {
    try { localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now())); } catch {}
    setShowInstall(false);
  };

  // ── Update toast ──────────────────────────────────────────────────
  useEffect(() => {
    const onUpdate = (e) => {
      const sw = e.detail?.waiting;
      if (!sw) return;
      setWaitingWorker(sw);
      toast('Yeni sürüm hazır', {
        description: 'Uygulamayı güncelleyerek en son değişiklikleri alın.',
        duration: 60000,
        action: {
          label: 'Güncelle',
          onClick: () => {
            try { sw.postMessage({ type: 'SKIP_WAITING' }); } catch { window.location.reload(); }
          },
        },
      });
    };
    window.addEventListener('pwa-update-ready', onUpdate);
    return () => window.removeEventListener('pwa-update-ready', onUpdate);
  }, []);

  // ── Offline bar ───────────────────────────────────────────────────
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  return (
    <>
      {offline && (
        <div
          role="status"
          className="fixed top-16 inset-x-0 z-50 bg-amber-500/95 text-slate-900 text-xs font-medium px-3 py-1.5 flex items-center justify-center gap-2 shadow-md"
          data-testid="pwa-offline-bar"
        >
          <WifiOff className="h-3.5 w-3.5" />
          <span>İnternet bağlantısı yok — değişiklikler şu an kaydedilemeyecek.</span>
        </div>
      )}
      {showInstall && installPromptEvent && (
        <div
          className="fixed bottom-4 right-4 z-50 w-[calc(100%-2rem)] max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl rounded-xl p-3 flex items-start gap-3"
          data-testid="pwa-install-banner"
        >
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
            <Download className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">OMS Portal'i Yükleyin</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Ana ekranınıza ekleyin — tek tıkla açılır, bildirimler native olur.
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Button size="sm" className="h-7 text-[11px]" onClick={triggerInstall} data-testid="pwa-install-accept">
                <Download className="h-3 w-3 mr-1" /> Yükle
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={dismissInstall} data-testid="pwa-install-dismiss">
                Daha Sonra
              </Button>
            </div>
          </div>
          <button onClick={dismissInstall} className="text-slate-400 hover:text-slate-600 shrink-0" aria-label="Kapat">
            <X className="h-4 w-4" />
          </button>
          {waitingWorker && null /* suppress unused warning */}
        </div>
      )}
    </>
  );
}
